/**
 * Normalized width / height / aspectRatio from persisted `Media.metadata` (JSON).
 * Reuses album pixel keys, generic width/height, and video ingest shapes — single source
 * for archive, event album, Memories, and hero heuristics.
 */

import {
  MEDIA_IMAGE_PIXEL_DIM_MAX,
  readAlbumImagePixelSizeFromMetadata,
} from "@/lib/events/album-image-pixel-metadata"

export type NormalizedMediaDimensions = {
  width?: number
  height?: number
  aspectRatio?: number
}

function recordFromMetadata(meta: unknown): Record<string, unknown> | null {
  if (meta == null || typeof meta !== "object" || Array.isArray(meta)) {
    return null
  }
  return meta as Record<string, unknown>
}

/** Positive pixel dimension from metadata (rounded; bounded). */
export function parsePositiveDisplayDimension(
  value: unknown,
): number | undefined {
  let n: number
  if (typeof value === "number") {
    if (!Number.isFinite(value) || value <= 0) return undefined
    n = value
  } else if (typeof value === "string" && value.trim()) {
    n = Number(value.trim())
    if (!Number.isFinite(n) || n <= 0) return undefined
  } else {
    return undefined
  }
  const rounded = Math.round(n)
  if (rounded < 1 || rounded > MEDIA_IMAGE_PIXEL_DIM_MAX) return undefined
  return rounded
}

/** Stored width/height ratio when no separate pixel pair exists. */
function parsePositiveAspectRatio(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) {
    return value
  }
  if (typeof value === "string" && value.trim()) {
    const n = Number(value.trim())
    if (Number.isFinite(n) && n > 0) return n
  }
  return undefined
}

function withComputedAspect(
  width: number,
  height: number,
): NormalizedMediaDimensions {
  const aspectRatio = width / height
  return {
    width,
    height,
    aspectRatio:
      Number.isFinite(aspectRatio) && aspectRatio > 0
        ? aspectRatio
        : undefined,
  }
}

/**
 * Image rows: prefer `imagePixelWidth` / `imagePixelHeight`, then generic `width` /
 * `height` / `naturalWidth` / `naturalHeight`, then standalone `aspectRatio`.
 */
export function normalizeImageMediaDimensionsFromMetadata(
  metadata: unknown,
): NormalizedMediaDimensions {
  const album = readAlbumImagePixelSizeFromMetadata(metadata)
  if (album) {
    return withComputedAspect(album.width, album.height)
  }
  const o = recordFromMetadata(metadata)
  if (!o) return {}
  const w =
    parsePositiveDisplayDimension(o.width) ??
    parsePositiveDisplayDimension(o.naturalWidth)
  const h =
    parsePositiveDisplayDimension(o.height) ??
    parsePositiveDisplayDimension(o.naturalHeight)
  if (w != null && h != null) {
    return withComputedAspect(w, h)
  }
  const directAr = parsePositiveAspectRatio(o.aspectRatio)
  if (directAr != null) return { aspectRatio: directAr }
  return {}
}

/**
 * Video rows: `width` / `height`, `videoWidth` / `videoHeight`, nested `video`,
 * then standalone `aspectRatio`.
 */
export function normalizeVideoMediaDimensionsFromMetadata(
  metadata: unknown,
): NormalizedMediaDimensions {
  const o = recordFromMetadata(metadata)
  if (!o) return {}
  const nestedVideo =
    o.video && typeof o.video === "object" && !Array.isArray(o.video)
      ? (o.video as Record<string, unknown>)
      : null
  const width =
    parsePositiveDisplayDimension(o.width) ??
    parsePositiveDisplayDimension(o.videoWidth) ??
    (nestedVideo ? parsePositiveDisplayDimension(nestedVideo.width) : undefined)
  const height =
    parsePositiveDisplayDimension(o.height) ??
    parsePositiveDisplayDimension(o.videoHeight) ??
    (nestedVideo ? parsePositiveDisplayDimension(nestedVideo.height) : undefined)
  if (width != null && height != null) {
    return withComputedAspect(width, height)
  }
  const directAr = parsePositiveAspectRatio(o.aspectRatio)
  if (directAr != null) return { aspectRatio: directAr }
  return {}
}
